import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgApi } from '../api/org.api.js';

/**
 * Tashkiliy birliklar uchun so'rov kalitlari.
 *
 * ## Nega alohida fayl
 *
 * Ilgari uch joyda AYNAN bir xil kalit (`['org', 'units', 'all']`) turardi,
 * lekin ular ikki XIL shakl saqlardi: `useScopeOptions` barcha sahifalarni
 * yig'ib MASSIV qo'yardi, buxgalteriya ekranlari esa bitta sahifani
 * `{ items, total, … }` OBYEKTI bo'lib qo'yardi.
 *
 * Natijasi ekranda shunday ko'rinardi: buxgalter avval `/accounting` ga
 * kirsa, kesh obyekt bilan to'lardi; keyin `/admin/users` ga o'tganda
 * `(units.data ?? []).filter is not a function` bilan butun sahifa qulardi.
 * Xato TARTIBGA bog'liq bo'lgani uchun u goh chiqib, goh chiqmasdi.
 *
 * Qoida: **so'rov kaliti shaklni nomlaydi**. Bir xil kalit ostida ikki xil
 * shakl yashay olmaydi, shuning uchun kalitlar ham, ularni o'qiydigan
 * hooklar ham shu yerda, bir joyda turadi.
 */
export const orgKeys = {
  /** Bitta sahifa, `{ items, total, … }` shaklida. */
  unitsPage: (limit: number) => ['org', 'units', 'page', limit] as const,
  /** BARCHA sahifalar, tekis MASSIV shaklida. */
  unitsFlat: () => ['org', 'units', 'flat'] as const,
};

/*
 * NOM JADVALI BITTA SAHIFADAN EMAS, HAMMASIDAN yig'iladi.
 *
 * Ilgari bu yerda bitta sahifa (200 ta — `paginationQuerySchema` dagi
 * eng katta hajm) so'ralardi, bazada esa besh yuzdan ortiq korxona bor.
 * Natijada 200 tadan keyingi korxonaning nomi HECH QACHON topilmasdi va
 * `nameOf` `null` qaytarardi.
 *
 * Oqibati jimgina emas, YOLG'ON edi: talabnoma kartochkasi «Biriktirilmagan
 * — xodimga ish joyi qo'yilmagan» deb yozardi, holbuki ish joyi qo'yilgan
 * va hujjatda `requesterOrgUnitId` turardi. Xodimni bor ayb bilan
 * ayblaydigan xabar.
 *
 * Aynan shu xato bir marta doira tanlagichida bo'lib o'tgan va o'shanda
 * `orgApi.listAllUnits` yozilgan edi (sahifalab yig'adi) — u shu yerda
 * ham ishlatilishi kerak edi.
 */

/**
 * Bitta sahifadagi birliklar — ro'yxat va `id → nom` jadvali birga.
 *
 * Ikkalasi bitta hookdan qaytadi, chunki ikki chaqiruvchi ikki xil narsani
 * so'raydi: prixod ekraniga filtr uchun RO'YXAT kerak, so'rovnoma ekraniga
 * esa satrda "#23" emas, "Qarshi PCH-5" chiqishi uchun NOM kerak. Ikki
 * alohida hook bo'lsa, ular yana ikki xil kalit ostida ikki xil shakl
 * saqlashga qaytardi — bu faylning sarlavhasidagi xato aynan shundan
 * boshlangan edi.
 */
export const useOrgUnits = () => {
  const units = useQuery({
    queryKey: orgKeys.unitsFlat(),
    queryFn: () => orgApi.listAllUnits(),
    staleTime: 300_000,
  });

  const items = useMemo(() => units.data ?? [], [units.data]);
  const nameById = useMemo(
    () => new Map(items.map((unit) => [unit.id, unit.name])),
    [items],
  );

  /** Nomi topilmasa `null` — chaqiruvchi o'zi qaror qiladi. */
  const nameOf = (id: string | null): string | null =>
    id === null ? null : (nameById.get(id) ?? null);

  /*
   * QISQA BELGI — "PCH-5", "ECH-3".
   *
   * To'liq nom ("5-yo'l mashinalari distansiyasi") kartochkaning bir
   * qatorini yeb qo'yadi va ro'yxatda o'nta kartochka yonma-yon turganda
   * ko'z ularni solishtira olmaydi. Qisqa belgi esa xodimlar kundalik
   * ishda ishlatadigan nom — hujjatda ham shunday yoziladi.
   *
   * DTIL dan kelmagan yozuvda `shortName` bo'sh bo'ladi, shuning uchun
   * zanjir: qisqa belgi → kod → to'liq nom. Oxirgisi bo'lmasligi mumkin
   * emas, ya'ni javob hech qachon "#23" bo'lib qolmaydi.
   *
   * STANSIYA bu qoidadan CHETDA — pastdagi izohga qarang.
   */
  const shortNameOf = (id: string | null): string | null => {
    if (id === null) return null;
    const unit = items.find((row) => row.id === id);
    if (unit === undefined) return null;

    /*
     * STANSIYADA qisqartma YO'Q — qisqa belgisi ham, kodi ham o'sha bitta
     * TARIF RAQAMI ("728036"). Uni yorliq qilib qo'ysak, kartochkada
     * ma'nosiz son turadi: odam "Jom" ni izlaydi, raqamni emas, va hujjat
     * qayerdan kelganini o'qiy olmaydi.
     *
     * Korxonada esa qisqartma haqiqiy qisqartma ("PCH-12") va u to'liq
     * nomdan tezroq o'qiladi — ya'ni qoida o'sha-o'sha, faqat stansiyada
     * `shortName` qisqartma bo'lmay qolgan.
     *
     * Raqam yo'qolmaydi: u `code` da turibdi va kerak bo'lgan joyda
     * (qidiruv, izoh) o'sha yerdan olinadi.
     */
    if (unit.type === 'STATION') return unit.name;

    const short = unit.shortName ?? null;
    return short !== null && short.trim() !== '' ? short : (unit.code ?? unit.name);
  };

  return { items, nameOf, shortNameOf };
};
